import type { ProductListItem } from '~/features/experiments/types/product-list.types';

/** Joins a before-record back to its enriched product row via the platform-native product id. */
export function getBeforeProductId(before: ProductListItem): string {
  return before.externalId;
}

/** Resolves the before-product's thumbnail — prefers the first Lily-processed image over the raw feed image. */
export function getBeforeImageUrl(before: ProductListItem): string | undefined {
  return before.optimizedImages?.[0] ?? before.imageLink;
}

/** Treats an empty string the same as "field absent" — the catalog API returns `""` rather than omitting the key. */
function emptyToUndefined(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  return value;
}

/** Per-field extractors for catalog fields that have a direct enriched-field equivalent. Fields
 *  absent from this map (e.g. `structuredTitle`, `score`) have no catalog-list source and always
 *  render "Not provided in feed" — see docs/enrichment-diff-table.md for the open item on score. */
const FIELD_EXTRACTORS: Record<string, (before: ProductListItem) => unknown> = {
  title: (b) => emptyToUndefined(b.title),
  description: (b) => emptyToUndefined(b.description),
  color: (b) => emptyToUndefined(b.color),
  material: (b) => emptyToUndefined(b.material),
  pattern: (b) => emptyToUndefined(b.pattern),
  googleProductCategory: (b) => emptyToUndefined(b.googleProductCategory),
  productType: (b) => (b.productTypes?.length ? b.productTypes.join(' > ') : undefined),
  productDetail: (b) => (b.productDetails?.length ? b.productDetails : undefined),
  productHighlight: (b) => (b.productHighlights?.length ? b.productHighlights : undefined),
};

/**
 * Resolves the pre-enrichment ("before") value for a given enriched field key.
 * Returns `undefined` when the catalog list has no equivalent field, so callers
 * render the "Not provided in feed" fallback rather than a false diff.
 */
export function getBeforeFieldValue(before: ProductListItem, key: string): unknown {
  return FIELD_EXTRACTORS[key]?.(before);
}
